/**
 * Local VDR indexing queue (upload tray + Ask Lens "X processing").
 *
 * Entries are tab-session only (sessionStorage). They must never live in
 * redux-persist/localStorage — that is what left "processing" badges stuck
 * for days after indexing had finished.
 */

export const INDEXING_STORAGE_KEY = 'vdr_indexing_files';
/** File still missing from keyword index after this age → failed (upload/RAG not started). */
export const VDR_INDEXING_MAX_AGE_MS = 15 * 60 * 1000;
/** Keyword done but RAG/tabular chunks still missing — allow long Azure/Celery backlogs. */
export const VDR_RAG_CATCHUP_MAX_AGE_MS = 150 * 60 * 1000; // 2h 30m
/** Poll until the session indexing badge would expire (must match isIndexingEntryActive). */
export const VDR_INDEX_STATUS_POLL_MAX_AGE_MS = VDR_RAG_CATCHUP_MAX_AGE_MS;
export const INDEXING_FAILED_MESSAGE = 'Indexing failed';

/**
 * Whether the client should keep polling index_status for this upload row.
 * Rows past the poll window are excluded. Failed rows stay eligible until the
 * RAG catch-up window ends so the tray can recover from premature 15m failures.
 */
export const shouldPollDocumentIndexStatus = (item, now = Date.now()) => {
    if (!item || item.id == null) return false;
    const startedAt = Number(item.startedAt) || 0;
    if (!startedAt) return item.status !== 'failed';
    return now - startedAt <= VDR_INDEX_STATUS_POLL_MAX_AGE_MS;
};

/** True when Azure RAG index has at least one chunk for this file (search-indexed). */
export const isAzureRagIndexed = row =>
    Boolean(row?.indexed) ||
    Boolean(row?.rag_ready) ||
    String(row?.status || '')
        .toLowerCase()
        .trim() === 'indexed';

/** Keyword index finished; waiting on RAG chunks (tabular path or RAG indexer). */
export const isKeywordReadyAwaitingRag = row => {
    if (Boolean(row?.keyword_ready)) return true;
    const status = String(row?.status || '')
        .toLowerCase()
        .trim();
    return status === 'processing' || status === 'processing_ai';
};

/**
 * Map an Azure index_status row to local UI state.
 *
 * With labelling paused, the API marks `indexed` / `rag_ready` when chunks exist.
 * Legacy API rows may still use `processing_ai` while `rag_ready` is true — treat
 * those as done so the tray and folder badges match Azure indexing.
 */
export const classifyAzureIndexStatus = (row, { startedAt, now = Date.now() } = {}) => {
    const status = String(row?.status || '')
        .toLowerCase()
        .trim();
    if (status === 'not_applicable' || status === 'not_found') {
        return 'done';
    }
    if (isAzureRagIndexed(row) || Boolean(row?.ai_processed)) {
        return 'done';
    }
    const started = Number(startedAt) || 0;
    const age = started > 0 ? now - started : 0;
    if (status === 'processing_ai') {
        return age > VDR_RAG_CATCHUP_MAX_AGE_MS ? 'failed' : 'processing_ai';
    }
    const failAfterMs = isKeywordReadyAwaitingRag(row)
        ? VDR_RAG_CATCHUP_MAX_AGE_MS
        : VDR_INDEXING_MAX_AGE_MS;
    if (age > failAfterMs) {
        return 'failed';
    }
    return 'indexing';
};

export const emptyUploadTray = () => ({
    items: [],
    collapsed: false,
    destinationLabel: '',
});

export const readIndexingStorage = () => {
    try {
        const raw = sessionStorage.getItem(INDEXING_STORAGE_KEY);
        const parsed = raw ? JSON.parse(raw) : [];
        return Array.isArray(parsed) ? parsed : [];
    } catch {
        return [];
    }
};

export const writeIndexingStorage = entries => {
    try {
        sessionStorage.setItem(INDEXING_STORAGE_KEY, JSON.stringify(entries || []));
    } catch {
        // ignore quota / private mode failures
    }
};

export const normalizeIndexingEntry = (item, now = Date.now()) => {
    if (!item || typeof item !== 'object') return null;
    if (item.id == null || item.wsId == null || !item.projectId) return null;
    const startedAt = Number(item.startedAt);
    const status = item.status === 'failed' ? 'failed' : 'indexing';
    return {
        id: Number(item.id),
        wsId: Number(item.wsId),
        projectId: String(item.projectId),
        // Legacy entries without startedAt expire immediately so stuck
        // spinners cannot loop forever across refreshes.
        startedAt: Number.isFinite(startedAt) && startedAt > 0 ? startedAt : 0,
        status,
    };
};

const matchesProject = (item, projectId) => {
    if (projectId == null || projectId === '') return true;
    return item.projectId == null || String(item.projectId) === String(projectId);
};

export const pruneExpiredIndexingEntries = (entries, now = Date.now()) =>
    (entries || [])
        .map(item => normalizeIndexingEntry(item, now))
        .filter(Boolean)
        .flatMap(item => {
            if (item.status === 'failed') return [item];
            // Legacy rows without startedAt are dropped so stuck spinners cannot return.
            if (!(item.startedAt > 0)) return [];
            // Hard cap only — success/failure otherwise comes from Azure index presence.
            if (now - item.startedAt > VDR_RAG_CATCHUP_MAX_AGE_MS) {
                return [{ ...item, status: 'failed' }];
            }
            return [item];
        });

export const isIndexingEntryActive = (item, now = Date.now()) => {
    const normalized = normalizeIndexingEntry(item, now);
    if (!normalized || normalized.status === 'failed') return false;
    if (!(normalized.startedAt > 0)) return false;
    return now - normalized.startedAt <= VDR_RAG_CATCHUP_MAX_AGE_MS;
};

export const getActiveIndexingFiles = (indexingFiles, { projectId, now = Date.now() } = {}) => {
    return (indexingFiles || []).filter(item => {
        if (!isIndexingEntryActive(item, now)) return false;
        return matchesProject(item, projectId);
    });
};

export const getFailedIndexingFiles = (indexingFiles, { projectId } = {}) =>
    (indexingFiles || []).filter(item => {
        const normalized = normalizeIndexingEntry(item);
        if (!normalized || normalized.status !== 'failed') return false;
        return matchesProject(normalized, projectId);
    });

export const activeIndexingFileIds = (indexingFiles, options) =>
    getActiveIndexingFiles(indexingFiles, options)
        .map(item => Number(item.id))
        .filter(Number.isFinite);

export const failedIndexingFileIds = (indexingFiles, options) =>
    getFailedIndexingFiles(indexingFiles, options)
        .map(item => Number(item.id))
        .filter(Number.isFinite);

export const expireStaleTrayItems = (items, now = Date.now()) =>
    (items || []).map(item => {
        if (item?.status !== 'indexing') return item;
        // Missing startedAt is treated as already expired (stuck persist leftovers).
        const started = Number(item.startedAt) || 0;
        if (!started) {
            return { ...item, status: 'ready', error: undefined };
        }
        if (now - started > VDR_RAG_CATCHUP_MAX_AGE_MS) {
            return { ...item, status: 'failed', error: INDEXING_FAILED_MESSAGE };
        }
        return item;
    });

/** Strip ephemeral upload/index state before redux-persist writes localStorage. */
export const stripPersistedDocumentsEphemeral = inboundState => {
    if (!inboundState || typeof inboundState !== 'object') return inboundState;
    return {
        ...inboundState,
        indexingFiles: [],
        uploadTray: emptyUploadTray(),
    };
};
